import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { DollarSign, TrendingUp, Clock, CheckCircle2, Package } from 'lucide-react';
import { useMerchantAuth } from '../../contexts/MerchantAuthContext';
import MerchantLayout from '../../components/MerchantLayout';
import { toast } from 'sonner';

export default function MerchantDashboardPage() {
  const { fetchWithAuth } = useMerchantAuth();
  const [stats, setStats] = useState({
    total_orders: 0,
    total_volume: 0,
    pending_orders: 0,
    completed_orders: 0,
    conversion_rate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/merchant/stats`);
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Error fetching stats');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar estadísticas');
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Órdenes',
      value: stats.total_orders,
      icon: Package,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Volumen Total',
      value: `$${stats.total_volume.toFixed(2)}`,
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Pendientes',
      value: stats.pending_orders,
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50'
    },
    {
      title: 'Completadas',
      value: stats.completed_orders,
      icon: CheckCircle2,
      color: 'text-[hsl(var(--brand-teal))]',
      bg: 'bg-[hsl(var(--brand-teal))]/10'
    }
  ];

  if (loading) {
    return (
      <MerchantLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-teal))] mx-auto"></div>
          <p className="mt-4 text-[hsl(var(--muted-foreground))]">Cargando estadísticas...</p>
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-dashboard-page">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
            Dashboard
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Vista general de tu actividad
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="p-6" data-testid={`merchant-stat-card-${index}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-[hsl(var(--brand-ink))]">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bg}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Welcome Card */}
        <Card className="p-8">
          <h2 className="text-xl font-semibold text-[hsl(var(--brand-ink))] mb-2">
            ¡Bienvenido a tu Portal de Merchant!
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-6">
            Desde aquí puedes gestionar todas tus órdenes de pago crypto, configurar webhooks,
            y acceder a tu API Key para integrar FluxiPay en tu aplicación.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-4 bg-[hsl(var(--accent))] rounded-lg">
              <p className="font-medium text-[hsl(var(--brand-ink))] mb-1">🚀 Comienza rápido</p>
              <p className="text-[hsl(var(--muted-foreground))]">Obtén tu API Key en Configuración</p>
            </div>
            <div className="p-4 bg-[hsl(var(--accent))] rounded-lg">
              <p className="font-medium text-[hsl(var(--brand-ink))] mb-1">🔔 Configura Webhooks</p>
              <p className="text-[hsl(var(--muted-foreground))]">Recibe notificaciones en tiempo real</p>
            </div>
            <div className="p-4 bg-[hsl(var(--accent))] rounded-lg">
              <p className="font-medium text-[hsl(var(--brand-ink))] mb-1">📈 Monitorea pagos</p>
              <p className="text-[hsl(var(--muted-foreground))]">Todas tus órdenes en un solo lugar</p>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-2">Tasa de Conversión</h3>
            <p className="text-3xl font-bold text-[hsl(var(--brand-teal))] mb-2">
              {stats.conversion_rate.toFixed(1)}%
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Pagos completados vs total de órdenes
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-2">Promedio por Orden</h3>
            <p className="text-3xl font-bold text-[hsl(var(--brand-ink))] mb-2">
              ${stats.total_orders > 0 ? (stats.total_volume / stats.total_orders).toFixed(2) : '0.00'}
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Valor promedio de transacción
            </p>
          </Card>
        </div>
      </div>
    </MerchantLayout>
  );
}
